import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { GridColDef } from '@mui/x-data-grid-premium';
import DataGrid from '../components/DataGrid/DataGrid';
import DialogWrapper from '../components/Dialog/DialogWrapper';
import { useGetCustomReportsByMetricQuery } from '../admin/requests/queries';
import {
  dialogContentSx,
  dialogDescriptionBoxSx,
  dialogMetricNameSx,
  dialogActionsSx,
  dialogOkButtonSx,
} from './styles';

interface MetricsInUseDialogProps {
  open: boolean;
  onClose: () => void;
  metricName: string;
  metricId: string;
  metricCount: number;
}

function MetricsInUseDialog({ open, onClose, metricName, metricId, metricCount }: MetricsInUseDialogProps) {
  const { t } = useTranslation();

  // Fetch custom reports for the metric (only when dialog is open)
  const { data: reports = [], isLoading } = useGetCustomReportsByMetricQuery(open && !!metricId, metricId);

  const reportsDialogColumns: GridColDef[] = [
    {
      field: 'reportName',
      headerName: t('sales.salesSummaryReport.report'),
      flex: 1,
      minWidth: 200,
    },
  ];

  const dialogReportRows = reports.map((report) => ({
    id: report.id,
    reportName: report.title,
  }));

  return (
    <DialogWrapper
      title={t('navItems.customMetricsHomePage.dialogHeading', {
        count: metricCount,
      })}
      dialogOptions={{
        open,
        onClose,
        maxWidth: 'sm',
        fullWidth: true,
      }}
      dataTestId='metrics-in-use-dialog'
    >
      <DialogContent sx={dialogContentSx}>
        <Box sx={dialogDescriptionBoxSx}>
          <Typography variant='body1' color='text.secondary'>
            {t('navItems.customMetricsHomePage.dialogDescription')}{' '}
            <Box component='span' sx={dialogMetricNameSx}>
              {metricName}
            </Box>
          </Typography>
        </Box>
        {isLoading ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
            <CircularProgress data-testid='reports-loading-spinner' />
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              columns={reportsDialogColumns}
              rows={dialogReportRows}
              hideFooter
              autoHeight={false}
              disableColumnMenu
              disableColumnFilter
              disableDensitySelector
              localeText={{
                toolbarQuickFilterPlaceholder: t('navItems.customMetricsHomePage.dialogSearchPlaceholder'),
              }}
              data-testid='reports-dialog-grid'
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button variant='contained' onClick={onClose} data-testid='dialog-ok-button' sx={dialogOkButtonSx}>
          {t('buttonText.ok')}
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
}

export default MetricsInUseDialog;
